/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, reatomMap, withInit } from '@reatom/framework'
import BigNumber from 'bignumber.js'
import * as DateF from 'date-fns'

import { Iterable, Map, pipe } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { type PairModel } from '@root/entities/pair'

import { reatomAggregatedPositionSizes } from './reatom-aggregated-position-sizes'
import { reatomPositionPnl } from './reatom-position-pnl'
import { reatomWalletPosition } from './reatom-wallet-position'

import type { WalletPositionModel } from './reatom-wallet-position'
import type { WalletId } from '@root/entities/wallet'
import type { UserApi } from '@root/shared/api'

class AggregatedPositionModelClass {}

export const reatomAggregatedPosition_ = ({
  dto,
  pair,
}: {
  dto: UserApi.AggregatedPositionDto
  pair: PairModel
}) => {
  const pairAddress = pair.address
  const network = Network.fromDto(dto.network)

  //#region State
  const positionsByWalletAtom = reatomMap(
    Map.create<WalletId, WalletPositionModel>(),
  ).pipe(
    withInit((ctx) => {
      return pipe(
        dto.positionsByWallet,
        Iterable.map((it) => reatomWalletPosition.create(ctx, it.position)),
        Iterable.toMap((it) => it.walletId),
      )
    }),
  )

  const monthChartAtom = atom(dto.monthChart)
  //#endregion

  //#region Computed
  const walletPositionsAtom = atom((ctx) => {
    return [...ctx.spy(positionsByWalletAtom).values()]
  })

  const oldestPositionAtom = atom((ctx) => {
    return pipe(
      ctx.spy(walletPositionsAtom),
      Iterable.minBy((it) => it.createdAt),
    )
  })

  const createdAtAtom = atom((ctx) => {
    const oldestPosition = ctx.spy(oldestPositionAtom)
    return oldestPosition?.createdAt ?? new Date(0)
  })

  const closedAtAtom = atom((ctx) => {
    const walletPositions = ctx.spy(walletPositionsAtom)
    if (!walletPositions.every((it) => ctx.spy(it.closedAt))) {
      return undefined
    }

    let closedAt = undefined
    for (const position of walletPositions) {
      const positionClosedAt = ctx.spy(position.closedAt)
      if (!positionClosedAt) {
        return undefined
      }
      if (!closedAt) {
        closedAt = positionClosedAt
        continue
      }
      if (DateF.isAfter(positionClosedAt, closedAt)) {
        closedAt = positionClosedAt
      }
    }
    return closedAt
  })

  const entryPriceUsdAtom = atom((ctx) => {
    const oldestPosition = ctx.spy(oldestPositionAtom)
    if (!oldestPosition) {
      return BigNumber(0)
    }
    return ctx.spy(oldestPosition.entryPriceUsd)
  })

  const entryPriceNativeAtom = atom((ctx) => {
    const oldestPosition = ctx.spy(oldestPositionAtom)
    if (!oldestPosition) {
      return BigNumber(0)
    }
    return ctx.spy(oldestPosition.entryPriceNative)
  })

  const entryMarketCapUsdAtom = atom((ctx) => {
    const oldestPosition = ctx.spy(oldestPositionAtom)
    if (!oldestPosition) {
      return BigNumber(0)
    }
    return ctx.spy(oldestPosition.entryMarketCapUsd)
  })

  const targetPriceUsdAtom = atom((ctx) => {
    const oldestPosition = ctx.spy(oldestPositionAtom)
    if (!oldestPosition) {
      return BigNumber(0)
    }
    return ctx.spy(oldestPosition.targetPriceUsd)
  })

  const targetPriceNativeAtom = atom((ctx) => {
    const oldestPosition = ctx.spy(oldestPositionAtom)
    if (!oldestPosition) {
      return BigNumber(0)
    }
    return ctx.spy(oldestPosition.targetPriceNative)
  })

  const targetMarketCapUsdAtom = atom((ctx) => {
    const oldestPosition = ctx.spy(oldestPositionAtom)
    if (!oldestPosition) {
      return BigNumber(0)
    }
    return ctx.spy(oldestPosition.targetMarketCapUsd)
  })

  const closePriceUsdAtom = atom((ctx) => {
    const oldestPosition = ctx.spy(oldestPositionAtom)
    if (!oldestPosition) {
      return undefined
    }
    return ctx.spy(oldestPosition.closePriceUsd)
  })
  const closePriceNativeAtom = atom((ctx) => {
    const oldestPosition = ctx.spy(oldestPositionAtom)
    if (!oldestPosition) {
      return undefined
    }
    return ctx.spy(oldestPosition.closePriceNative)
  })
  const closeMarketCapUsdAtom = atom((ctx) => {
    const oldestPosition = ctx.spy(oldestPositionAtom)
    if (!oldestPosition) {
      return undefined
    }
    return ctx.spy(oldestPosition.closeMarketCapUsd)
  })

  const hiddenAtom = atom((ctx) => {
    return ctx.spy(walletPositionsAtom).every((it) => ctx.spy(it.hidden))
  })

  const {
    boughtSizeUsd,
    boughtSizeNative,
    boughtSizeCurrency,
    boughtTransactions,
    soldSizeUsd,
    soldSizeNative,
    soldSizeCurrency,
    soldTransactions,
    remainingSizeCurrency,
    remainingSizeUsd,
    remainingSizeNative,
  } = reatomAggregatedPositionSizes({
    walletPositionsAtom,
  })

  const priceAtom = pair.priceRealtime

  const pnl = reatomPositionPnl({
    priceAtom,
    entryPriceUsdAtom,
    entryPriceNativeAtom,
    remainingSizeCurrencyAtom: remainingSizeCurrency,
    soldSizeUsdAtom: soldSizeUsd,
    soldSizeNativeAtom: soldSizeNative,
    soldSizeCurrencyAtom: soldSizeCurrency,
  })
  //#endregion

  const updateFromDtoAction = action(
    (ctx, dto: UserApi.AggregatedPositionDto) => {
      const byWallet = ctx.get(positionsByWalletAtom)
      for (const position of byWallet.values()) {
        const positionDto = dto.positionsByWallet.find(
          (it) => it.walletId === position.walletId,
        )
        if (!positionDto) {
          positionsByWalletAtom.delete(ctx, position.walletId)
          continue
        }

        position.updateFromDto(ctx, positionDto.position)
      }

      const positionsToAdd = dto.positionsByWallet.filter(
        (it) => !byWallet.has(it.walletId),
      )

      for (const positionDto of positionsToAdd) {
        const position = reatomWalletPosition.create(ctx, positionDto.position)
        positionsByWalletAtom.set(ctx, position.walletId, position)
      }

      pair.updateFromDto(ctx, dto.pair)
      monthChartAtom(ctx, dto.monthChart)
    },
  )

  const model = {
    pairAddress,
    pair,
    network,
    walletPositions: walletPositionsAtom,
    positionsByWallet: positionsByWalletAtom,
    hidden: hiddenAtom,

    createdAt: createdAtAtom,
    closedAt: closedAtAtom,
    monthChart: monthChartAtom,

    entryPriceUsd: entryPriceUsdAtom,
    entryPriceNative: entryPriceNativeAtom,
    entryMarketCapUsd: entryMarketCapUsdAtom,

    targetPriceUsd: targetPriceUsdAtom,
    targetPriceNative: targetPriceNativeAtom,
    targetMarketCapUsd: targetMarketCapUsdAtom,

    closePriceUsd: closePriceUsdAtom,
    closePriceNative: closePriceNativeAtom,
    closeMarketCapUsd: closeMarketCapUsdAtom,

    boughtSizeUsd,
    boughtSizeNative,
    boughtSizeCurrency,
    boughtTransactions,

    soldSizeUsd,
    soldSizeNative,
    soldSizeCurrency,
    soldTransactions,

    remainingSizeCurrency,
    remainingSizeUsd,
    remainingSizeNative,

    ...pnl,
    updateFromDto: updateFromDtoAction,
  }

  Object.setPrototypeOf(model, AggregatedPositionModelClass.prototype)

  return model
}
export interface AggregatedPositionModel
  extends ReturnType<typeof reatomAggregatedPosition_> {}

export const reatomAggregatedPosition = ({
  dto,
  pair,
}: {
  dto: UserApi.AggregatedPositionDto
  pair: PairModel
}) => {
  return reatomAggregatedPosition_({ dto, pair })
}

reatomAggregatedPosition.is = (
  position: AggregatedPositionModel | WalletPositionModel,
): position is AggregatedPositionModel => {
  return Boolean((position as AggregatedPositionModel).positionsByWallet)
}
